import posthog from 'posthog-js';
import { create } from 'zustand';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { THEME_STORAGE_KEY, ThemeEnum } from '@/Domain/enum/Theme.enum';

type ResolvedTheme = ThemeEnum.Dark | ThemeEnum.Light;

type ThemeState = {
  theme: ThemeEnum;
};

type ThemeActions = {
  setTheme: (theme: ThemeEnum) => void;
  syncTheme: () => void;
  getResolvedTheme: () => ResolvedTheme;
  isDarkTheme: () => boolean;
};

const THEME_VALUES = new Set(Object.values(ThemeEnum));

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') {
    return ThemeEnum.Light;
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? ThemeEnum.Dark
    : ThemeEnum.Light;
}

export function getStoredTheme(): ThemeEnum {
  if (typeof window === 'undefined') {
    return ThemeEnum.System;
  }

  const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
  return THEME_VALUES.has(storedTheme as ThemeEnum) ? (storedTheme as ThemeEnum) : ThemeEnum.System;
}

export function resolveTheme(theme: ThemeEnum): ResolvedTheme {
  return theme === ThemeEnum.System ? getSystemTheme() : theme;
}

export const useThemeStore = create<ThemeState & ThemeActions>((set, get) => ({
  theme: getStoredTheme(),
  setTheme: (theme: ThemeEnum) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    }

    posthog.capture(PostHogEventEnum.ThemeChanged, { theme });
    set({ theme });
  },
  syncTheme: () => {
    set({ theme: getStoredTheme() });
  },
  getResolvedTheme: () => resolveTheme(get().theme),
  isDarkTheme: () => get().getResolvedTheme() === ThemeEnum.Dark,
}));
